import type { MutableRefObject } from 'react'
import { useCallback } from 'react'

import {
  POPOVER_AVAILABLE_HEIGHT_PROPERTY,
  POPOVER_EMPTY_CLIENT_RECT,
} from '../popoverConstants'
import type {
  BoundaryViolations,
  ContentLocation,
  ContentLocationGetter,
  PopoverAlign,
  PopoverPosition,
  PopoverRect,
  PopoverState,
} from '../popoverTypes'
import { getNewPopoverRect, getNudgedPopoverRect } from '../utils'
import { useElementRef } from './useElementRef'

const POPOVER_STYLE: Partial<CSSStyleDeclaration> = {
  position: 'fixed',
  overflow: 'visible',
  top: '0px',
  left: '0px',
  maxWidth: '95vw',
}

const SCOUT_STYLE: Partial<CSSStyleDeclaration> = {
  position: 'fixed',
  top: '0px',
  left: '0px',
  width: '0px',
  height: '0px',
  visibility: 'hidden',
}

export const usePopover = ({
  isOpen,
  childRef,
  positions,
  containerClassName,
  parentElement,
  contentLocation,
  align,
  padding,
  reposition,
  boundaryInset,
  boundaryElement,
  onPositionPopover,
}: {
  isOpen: boolean
  childRef: MutableRefObject<HTMLElement | null>
  positions: PopoverPosition[]
  containerClassName: string
  parentElement: HTMLElement
  contentLocation?: ContentLocationGetter | ContentLocation
  align: PopoverAlign
  padding: number
  reposition: boolean
  boundaryInset: number
  boundaryElement: HTMLElement
  onPositionPopover: (popoverState: PopoverState) => void
}) => {
  const popoverRef = useElementRef(containerClassName, POPOVER_STYLE)
  const scoutRef = useElementRef('popover-scout', SCOUT_STYLE)

  const positionPopover = useCallback(
    ({
      positionIndex = 0,
      parentRect = parentElement.getBoundingClientRect(),
      childRect = childRef.current?.getBoundingClientRect(),
      scoutRect = scoutRef.current.getBoundingClientRect(),
      popoverRect = popoverRef.current.getBoundingClientRect(),
      boundaryRect = boundaryElement === parentElement
        ? parentRect
        : boundaryElement.getBoundingClientRect(),
    }: {
      positionIndex?: number
      parentRect?: PopoverRect
      childRect?: PopoverRect
      scoutRect?: PopoverRect
      popoverRect?: PopoverRect
      boundaryRect?: PopoverRect
    } = {}) => {
      if (!childRect || !parentRect || !isOpen) {
        return
      }

      const availableHeight = Math.max(
        boundaryRect.height - 2 * boundaryInset,
        0
      )
      popoverRef.current.style.setProperty(
        POPOVER_AVAILABLE_HEIGHT_PROPERTY,
        `${availableHeight}px`
      )

      if (contentLocation) {
        const { top: inputTop, left: inputLeft } =
          typeof contentLocation === 'function'
            ? contentLocation({
                childRect,
                popoverRect,
                parentRect,
                boundaryRect,
                padding,
                nudgedTop: 0,
                nudgedLeft: 0,
                boundaryInset,
                violations: POPOVER_EMPTY_CLIENT_RECT,
                hasViolations: false,
              })
            : contentLocation

        const left = Math.round(parentRect.left + inputLeft - scoutRect.left)
        const top = Math.round(parentRect.top + inputTop - scoutRect.top)

        popoverRef.current.style.transform = `translate(${left}px, ${top}px)`

        onPositionPopover({
          childRect,
          popoverRect,
          parentRect,
          boundaryRect,
          padding,
          nudgedTop: 0,
          nudgedLeft: 0,
          boundaryInset,
          violations: POPOVER_EMPTY_CLIENT_RECT,
          hasViolations: false,
        })

        return
      }

      const isExhausted = positionIndex === positions.length
      const position =
        (isExhausted ? positions[0] : positions[positionIndex]) ?? 'bottom'

      const { rect, boundaryViolation } = getNewPopoverRect(
        {
          childRect,
          popoverRect,
          boundaryRect,
          position,
          align,
          padding,
          reposition,
        },
        boundaryInset
      )

      if (boundaryViolation && reposition && !isExhausted) {
        positionPopover({
          positionIndex: positionIndex + 1,
          childRect,
          popoverRect,
          parentRect,
          boundaryRect,
        })
        return
      }

      const { top, left, width, height } = rect
      const shouldNudge = reposition && !isExhausted
      const { left: nudgedLeft, top: nudgedTop } = getNudgedPopoverRect(
        rect,
        boundaryRect,
        boundaryInset
      )

      let finalTop = top
      let finalLeft = left

      if (shouldNudge) {
        finalTop = nudgedTop
        finalLeft = nudgedLeft
      } else if (reposition && height <= availableHeight) {
        // No position fits, but the popover does: keep its side and slide it
        // inside the boundary rather than leave its end out of reach.
        finalTop = nudgedTop
      }

      finalTop = Math.round(finalTop - scoutRect.top)
      finalLeft = Math.round(finalLeft - scoutRect.left)

      popoverRef.current.style.transform = `translate(${finalLeft}px, ${finalTop}px)`

      const potentialViolations: BoundaryViolations = {
        top: boundaryRect.top + boundaryInset - finalTop,
        left: boundaryRect.left + boundaryInset - finalLeft,
        right: finalLeft + width - boundaryRect.right + boundaryInset,
        bottom: finalTop + height - boundaryRect.bottom + boundaryInset,
      }

      onPositionPopover({
        childRect,
        popoverRect: {
          top: finalTop,
          left: finalLeft,
          width,
          height,
          right: finalLeft + width,
          bottom: finalTop + height,
        },
        parentRect,
        boundaryRect,
        position,
        align,
        padding,
        nudgedTop: nudgedTop - top,
        nudgedLeft: nudgedLeft - left,
        boundaryInset,
        violations: {
          top: potentialViolations.top <= 0 ? 0 : potentialViolations.top,
          left: potentialViolations.left <= 0 ? 0 : potentialViolations.left,
          right: potentialViolations.right <= 0 ? 0 : potentialViolations.right,
          bottom:
            potentialViolations.bottom <= 0 ? 0 : potentialViolations.bottom,
        },
        hasViolations:
          potentialViolations.top > 0 ||
          potentialViolations.left > 0 ||
          potentialViolations.right > 0 ||
          potentialViolations.bottom > 0,
      })
    },
    [
      parentElement,
      childRef,
      scoutRef,
      popoverRef,
      boundaryElement,
      isOpen,
      contentLocation,
      positions,
      align,
      padding,
      reposition,
      boundaryInset,
      onPositionPopover,
    ]
  )

  return {
    positionPopover,
    popoverRef,
    scoutRef,
  }
}
